/**
 * The draft and its earlier steps and inputs, so edits to them can be undone. Typing in one field
 * (edits with the same group, close together) undoes as one edit; the last 50 are kept. Name, slug and
 * description are not part of it: undo never takes back what was typed for them. Nothing here is saved.
 */

import type { CustomToolDraft } from '../custom-tool-draft';

type StepsState = Pick<CustomToolDraft, 'inputs' | 'steps'>;

const LIMIT = 50;
const GROUP_MS = 800;

interface DraftHistory {
	draft: CustomToolDraft;
	past: StepsState[];
	last: { group: string; at: number } | null;
}

type DraftAction =
	| { type: 'edit'; edit: (draft: CustomToolDraft) => CustomToolDraft; at: number; group?: string }
	| { type: 'undo' };

export const startHistory = (draft: CustomToolDraft): DraftHistory => ({ draft, past: [], last: null });

export const draftHistory = (state: DraftHistory, action: DraftAction): DraftHistory => {
	if (action.type === 'undo') {
		const previous = state.past[state.past.length - 1];
		return previous ? { draft: { ...state.draft, ...previous }, past: state.past.slice(0, -1), last: null } : state;
	}
	const next = action.edit(state.draft);
	if (next === state.draft) return state;
	const stepsChanged = next.steps !== state.draft.steps || next.inputs !== state.draft.inputs;
	if (!stepsChanged) return { ...state, draft: next };
	const grouped = !!action.group && action.group === state.last?.group && action.at - state.last.at < GROUP_MS;
	const { inputs, steps } = state.draft;
	const past = grouped ? state.past : [...state.past, { inputs, steps }].slice(-LIMIT);
	return { draft: next, past, last: action.group ? { group: action.group, at: action.at } : null };
};
